import React from 'react';
import { Route, Switch } from 'react-router';
import { AppLayout } from './components/AppLayout/AppLayout';
import { loadTheme, initializeIcons } from '@fluentui/react';
import Dashboard from './components/Dashboard/Dashboard';
import AnnouncementManagement from './components/Announcements/AnnouncementManagement';
import UserManagement from './components/UserManagement/UserManagement';
import ChangePassword from './components/ChangePassword/ChangePassword';
import Login from './components/Login/Login';
import { BrowserRouter } from 'react-router-dom';
import { IUser } from './models/IUser';
import { IRegionDefaultPosition, IRoleDTO } from './models';
import { LanguageContext } from './LanguageContext';
import { germanLat, germanLng } from './constants/StaticVariables';

interface IAppState {
  user?: IUser;
}

const theme = {
  themePrimary: '#005537',
  themeLighterAlt: '#ceeae1',
  themeLighter: '#a6d8c6',
  themeLight: '#82c5ad',
  themeTertiary: '#62b296',
  themeSecondary: '#469f80',
  themeDarkAlt: '#2e8d6c',
  themeDark: '#1b7a59',
  themeDarker: '#0b6747',
  neutralLighterAlt: '#faf9f8',
  neutralLighter: '#f3f2f1',
  neutralLight: '#edebe9',
  neutralQuaternaryAlt: '#e1dfdd',
  neutralQuaternary: '#d0d0d0',
  neutralTertiaryAlt: '#c8c6c4',
  neutralTertiary: '#a19f9d',
  neutralSecondary: '#605e5c',
  neutralSecondaryAlt: '#8a8886',
  neutralPrimaryAlt: '#3b3a39',
  neutralPrimary: '#323130',
  neutralDark: '#201f1e',
  black: '#000000',
  white: '#ffffff',
}

export default class App extends React.Component<{}, IAppState> {
  static displayName = App.name;
  static contextType = LanguageContext;
  static context: React.ContextType<typeof LanguageContext>;

  constructor(props: any) {
    super(props);

    this.state = {

    }

    this.init();
  }

  private init() {
    initializeIcons();
    loadTheme({
      palette: theme
    });
  }

  public componentDidMount(): void {
    this.hydrateLanguageForAuthenticatedUser();
  }

  public render(): React.ReactElement<{}> {
    const token = this.getToken();

    if (!token) {
      return <AppLayout>
        <Login setToken={(token) => { this.setToken(token); }} />
      </AppLayout>
    }

    return (
      <AppLayout>
        <BrowserRouter>
          <Switch>
            <Route exact path="/">
              <Dashboard />
            </Route>
            <Route path="/brugeradministration">
              <UserManagement />
            </Route>
            <Route path="/beskeder">
              <AnnouncementManagement />
            </Route>
            <Route path="/skift-adgangskode">
              <ChangePassword />
            </Route>
          </Switch>
        </BrowserRouter>
      </AppLayout>
    );
  }
  static IsAuthenticated: boolean = false;
  static UserRoles: IRoleDTO[] = [];
  static CurrentUserRegionDefaultPosition: IRegionDefaultPosition;
  static Username: string = "";
  static SupplierId: string = "";
  static IsGermanUser: boolean = false;

  private setToken(user: IUser) {
    App.IsGermanUser = this.resolveIsGermanUser(user as any);

    if (this.context && this.context.setLanguage) {
      this.context.setLanguage(App.IsGermanUser ? 'de' : 'da');
    }

    localStorage.setItem('hd_token', JSON.stringify(user));
    this.setState({ user: user });
  }

  private getToken(): string | null {
    const tokenString = localStorage.getItem('hd_token') as string;
    const user = JSON.parse(tokenString) as IUser;

    if (user == null) {
      return null;
    }

    App.IsAuthenticated = true;
    App.Username = user?.name;
    App.SupplierId = user?.supplierId;
    App.UserRoles = user.roles;
    App.CurrentUserRegionDefaultPosition = user.regionDefaultPosition;
    App.IsGermanUser = this.resolveIsGermanUser(user as any);
    return user?.token;
  }

  private hydrateLanguageForAuthenticatedUser(): void {
    if (!this.context || !this.context.setLanguage) {
      return;
    }

    const tokenString = localStorage.getItem('hd_token') as string;
    if (!tokenString) {
      return;
    }

    const user = JSON.parse(tokenString) as IUser;
    const targetLanguage = this.resolveIsGermanUser(user) ? 'de' : 'da';

    if (this.context.language !== targetLanguage) {
      this.context.setLanguage(targetLanguage);
    }
  }

  private resolveIsGermanUser(user: IUser): boolean {
    const regionDefaultPosition = user?.regionDefaultPosition?.defaultPosition;
    if (!regionDefaultPosition || regionDefaultPosition.length < 2) {
      return false;
    }

    const [lat, lng] = regionDefaultPosition;
    return lat === germanLat && lng === germanLng;
  }

  public static IsUserInRole(role: string): boolean {
    if (!App.UserRoles) {
      return false;
    }

    return App.UserRoles.filter(d => d.name.indexOf(role) > -1).length == 1;
  }
}
